/**
 * Company-scope staff token reads.
 *
 * A company-scope staff member holds ONE token balance PER LOCATION, and each
 * location prices its own token, in its own currency. `getLocationStaffTokenConfig`
 * in staffService reads one location at a time (page_size 1, no
 * `skipLocationFilter`), which a company profile would have to fire once per
 * row; this reads every accessible config in a single request and exposes only
 * the token value, which is all the per-location money column needs.
 */

import { apiClient } from '@/shared/services/api';

interface BackendLocationTokenValue {
  locationId?: string;
  location?: string;
  tokenValueAmount?: string;
  tokenValueCurrency?: string;
  enabled?: boolean;
}

export interface LocationTokenValue {
  locationId: string;
  /** 0 when the location never priced its token — money is then unknown, not free. */
  tokenValueAmount: number;
  /** Empty when the backend has no currency for it; never defaulted to EUR. */
  tokenValueCurrency: string;
  enabled: boolean;
}

// ── Per-staff cross-location summary ─────────────────────────────────────────

/**
 * What ONE staff member holds and owes across every location, computed by the
 * server (`staff-token-balances/staff-summary/`).
 *
 * The totals used to be summed in the browser over whatever rows the balance
 * list returned, and the coverage line counted those same rows — so a viewer
 * whose access was narrower than the staff member's footprint read a confident
 * total with no way to know it was partial. `coverage` now comes from the
 * server, which is the only party that knows what it filtered out.
 *
 * The endpoint already answers in camelCase, so `apiClient`'s snake→camel pass
 * is a no-op over this body.
 */
/**
 * Which rule set the debt cap in force at a location.
 *
 * The server resolves this through the very functions the spending path calls
 * (`resolve_token_spend_capacity` / `debt_limit_with_source`), so a refused
 * charge and this screen can never name different reasons. Never recompute the
 * precedence in the browser — that drift is the whole reason the field exists.
 */
export type DebtLimitSource = 'personal_override' | 'company_policy' | 'location_config';

/** The rule governing ONE location's row. */
export interface StaffTokenRowPolicy {
  /**
   * The location is on this employee's block list. It sits OUTSIDE the capacity
   * fields exactly as it does at runtime: the block bars the site before any cap
   * is read, so a blocked row can still report the headroom it would have had.
   */
  blocked: boolean;
  /** The location configured staff tokens at all. */
  tokensEnabled: boolean;
  debtAllowed: boolean;
  /** `null` means UNLIMITED — not "unknown", not zero. */
  debtLimit: number | null;
  /** Remaining headroom before the cap refuses a charge. `null` = unlimited. */
  debtCapacity: number | null;
  /** Most tokens a single charge here could cover. `null` = unlimited. */
  maxTokensCovered: number | null;
  debtLimitSource: DebtLimitSource;
}

/** The inheritance, stated once above the table. */
export interface StaffTokenPolicyBanner {
  scopeType: string;
  companyPolicyEnabled: boolean;
  /** `null` when there is no company policy at all. */
  companyDebtEnabled: boolean | null;
  companyDebtLimit: number | null;
  /** Both `null` and "unlimited" render as no number — this tells them apart. */
  hasPersonalDebtOverride: boolean;
  personalDebtLimit: number | null;
  blockedLocationCount: number;
  locationCount: number;
}

interface BackendStaffTokenSummaryRow {
  locationId: string;
  locationName?: string;
  availableTokens: number;
  debtTokens: number;
  lastGrantAt?: string | null;
  /** False for a blocked site the employee never spent at. */
  hasBalance?: boolean;
  /** Decimal string, or null when the location never priced its token. */
  tokenValueAmount?: string | null;
  tokenValueCurrency?: string | null;
  policy?: StaffTokenRowPolicy;
}

interface BackendStaffTokenSummary {
  staffId: string;
  totals: {
    locationCount: number;
    totalAvailableTokens: number;
    totalDebtTokens: number;
    locationsWithAvailable: number;
    locationsWithDebt: number;
    blockedLocationCount?: number;
  };
  byLocation: BackendStaffTokenSummaryRow[];
  coverage: {
    locationCount: number;
    accessibleLocationCount: number;
    seesAllLocations: boolean;
  };
  policy?: StaffTokenPolicyBanner;
}

export interface StaffTokenSummaryRow {
  locationId: string;
  locationName: string;
  availableTokens: number;
  debtTokens: number;
  lastGrantAt: string | null;
  /**
   * Whether a balance row exists behind these numbers. False for a blocked site
   * the employee never spent at — "0 available" and "no balance here" are not
   * the same statement, and the table renders them differently.
   */
  hasBalance: boolean;
  /** `null` when the location never priced its token — unknown, not free. */
  tokenValueAmount: number | null;
  /** Empty when the backend has no currency for it; never defaulted to EUR. */
  tokenValueCurrency: string;
  /** The rule in force here, resolved by the server. Never recomputed locally. */
  policy: StaffTokenRowPolicy | null;
}

export interface StaffTokenSummary {
  staffId: string;
  totals: {
    /** Locations behind these totals, per the server. Balance rows only. */
    locationCount: number;
    availableTokens: number;
    debtTokens: number;
    locationsWithAvailable: number;
    locationsWithDebt: number;
    /** Visible locations this employee is blocked at — balance row or not. */
    blockedLocationCount: number;
  };
  byLocation: StaffTokenSummaryRow[];
  coverage: {
    locationCount: number;
    accessibleLocationCount: number;
    seesAllLocations: boolean;
  };
  /** `null` only if the server predates the banner. */
  policy: StaffTokenPolicyBanner | null;
}

export async function getStaffTokenSummary(staffId: string): Promise<StaffTokenSummary> {
  const response = await apiClient.get<BackendStaffTokenSummary>(
    '/api/v1/staff-token-balances/staff-summary/',
    // The endpoint is deliberately cross-location; letting the active location
    // be injected would collapse it to a single site.
    { params: { staff_id: staffId }, skipLocationFilter: true },
  );
  const body = response.data;
  return {
    staffId: body.staffId,
    totals: {
      locationCount: body.totals.locationCount,
      availableTokens: body.totals.totalAvailableTokens,
      debtTokens: body.totals.totalDebtTokens,
      locationsWithAvailable: body.totals.locationsWithAvailable,
      locationsWithDebt: body.totals.locationsWithDebt,
      blockedLocationCount: body.totals.blockedLocationCount ?? 0,
    },
    byLocation: (body.byLocation ?? []).map((row) => ({
      locationId: row.locationId,
      locationName: row.locationName ?? '',
      availableTokens: row.availableTokens,
      debtTokens: row.debtTokens,
      lastGrantAt: row.lastGrantAt ?? null,
      // Absent on an older server; a row that arrived at all had a balance
      // behind it back then, since blocked-only rows did not exist.
      hasBalance: row.hasBalance ?? true,
      tokenValueAmount: row.tokenValueAmount == null ? null : Number(row.tokenValueAmount),
      tokenValueCurrency: row.tokenValueCurrency ?? '',
      policy: row.policy ?? null,
    })),
    coverage: body.coverage,
    policy: body.policy ?? null,
  };
}

export async function getLocationTokenValues(): Promise<LocationTokenValue[]> {
  const response = await apiClient.get<{ results: BackendLocationTokenValue[] }>(
    '/api/v1/staff-token-configs/',
    // Company scope has no active location, but a stale one would narrow this
    // to a single site. 200 is the server's max_page_size (StandardPagination).
    { params: { page_size: 200 }, skipLocationFilter: true },
  );
  return (response.data.results ?? []).map((config) => ({
    locationId: config.locationId ?? config.location ?? '',
    tokenValueAmount: Number(config.tokenValueAmount ?? 0),
    tokenValueCurrency: config.tokenValueCurrency ?? '',
    enabled: config.enabled ?? false,
  }));
}
